import "bootstrap/dist/css/bootstrap.min.css";
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router";
import { Modal, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { ReactComponent as AddIcon } from '../../Assets/img/AddIcon.svg';
import { ReactComponent as Error } from '../../Assets/img/Error.svg';
import { ReactComponent as ResetIcon } from '../../Assets/img/Reset-primary-icon.svg';
import AuthenticationService from "../../Services/AuthenticationService";
import PartnerService from "../../Services/PartnerService";
import AuthContext from '../AuthContextComponent/AuthContext';
import LoadingIndicator from "../CommonComponent/LoadingIndicator";


/**
 * Renders add partner pop up to register partner
 * @component
 * @param {Event} props.onClose() To close pop up
 * @param {Event} props.onShowMessage("message") To show message in pop up
 */
function AddPartner(props) {
    const authCtx = useContext(AuthContext);
    let navigate = useNavigate();

    //#region States

    const [isLoading, setIsLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [error, setError] = useState("");
    const [messagePopup, setMessagePopup] = useState(false);

    //#endregion

    //#region Refs

    const countryNameRef = useRef();
    const userNameRef = useRef();
    const partnerNameRef = useRef();
    const addressRef = useRef();
    const contactNumberRef = useRef();
    const emailRef = useRef();
    const stateRef = useRef();
    const localityRef = useRef();
    const organizationNameRef = useRef();
    const commonNameRef = useRef();

    //#endregion

    //#region useEffect hook

    /**
    * Calls partner service to fetch all countries
    * Runs only on the first render
    */
    useEffect(() => {
        PartnerService.GetCountries().then((response) => {
            if (response.response == null) {
                setError(response.errors[0].message);
            } else {
                setCountries(response.response);
            }
        }).catch((err) => {
            setError(err.data);
        });
    }, [])

    //#endregion

    /**
    * Closes the form when rendered standalone (no parent modal manager to hand
    * onClose to, e.g. the /registerPartner bootstrap route) -- there's nothing
    * useful left to do as a logged-in Admin with no partner registered, so this
    * logs out and returns to the login screen, matching Navigation.js's logoutHandler.
    */
    function standaloneCloseHandler() {
        authCtx.logout();
        AuthenticationService.logout()
            .then(() => { })
            .catch(() => { });
        navigate("/login");
    }

    //#region Register Partner

    /**
    * Calls partner service to register partner
    * @param   {Event} event  User action (on submitting form)
    */
    async function registerPartner(event) {
        event.preventDefault();
        setIsLoading(true);

        let registerPartnerDTO = {
            CountryName: countryNameRef.current.value,
            UserName: userNameRef.current.value,
            PartnerName: partnerNameRef.current.value,
            Address: addressRef.current.value,
            ContactNumber: contactNumberRef.current.value,
            EmailId: emailRef.current.value,
            State: stateRef.current.value == "" ? null : stateRef.current.value,
            Locality: localityRef.current.value == "" ? null : localityRef.current.value,
            OrganizationName: organizationNameRef.current.value,
            CommonName: commonNameRef.current.value
        };

        PartnerService.registerPartner(registerPartnerDTO)
            .then((response) => {
                if (response.response == null) {
                    setError(response.errors[0].message);
                    setMessagePopup(true);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);

                    // Only auto-select this partner as the active tenant when it's the very
                    // first one (nothing else to select) -- registering an additional partner
                    // while one is already active must not silently switch the admin's tenant.
                    const wasTenantSelected = authCtx.isTenantSelected;
                    if (!wasTenantSelected) {
                        const countryName = countryNameRef.current.value;
                        const partnerName = partnerNameRef.current.value;
                        authCtx.Tenant();
                        authCtx.selectCountry(countryName);
                        authCtx.selectPartner(partnerName);
                    }

                    if (props.onClose) {
                        props.onClose();
                        props.onShowMessage("Partner registered sucessfully.");
                    } else if (!wasTenantSelected) {
                        // Rendered standalone via the /registerPartner route (no modal parent
                        // to close back into, e.g. the first-ever bootstrap registration) --
                        // go straight into the app instead.
                        navigate("/Devices");
                    } else {
                        // Standalone route, but the admin already had a tenant selected --
                        // registering another partner didn't change that, so there's nothing
                        // to navigate into; just return to the Partners list.
                        navigate("/Partners");
                    }
                }
            })
            .catch((err) => {
                setError(err.response.data.errors[0].message);
                setMessagePopup(true);
                setIsLoading(false);
            });
    }

    //#endregion

    return (
        <Modal size="lg" centered={true} show={true} onHide={props.onClose || standaloneCloseHandler}>
            <ModalHeader className="AddDeviceHeader" style={{ border: "none" }} closeButton >
                <ModalTitle className="Header">
                    <strong>Register Partner</strong>
                </ModalTitle>
            </ModalHeader>
            <Modal.Body style={{ padding: '0' }}>
                {messagePopup && (
                    <div className="alert alert-warning"><Error /><span className="message-popup-title">{error}</span></div>
                )}
                <form onSubmit={registerPartner}>
                    <div style={{ padding: '3%' }}>
                        <div className="register-partner-row-inputs">
                            <div className="register-partner-row-input" style={{ marginRight: "2%" }}>
                                <label for="country" className="label">Country</label>
                                <div>
                                    <input id="country" list="countries" type="text" className="InputField" placeholder="-Text-" maxlength="30" ref={countryNameRef} required />
                                    <datalist id="countries">
                                        {countries?.map((country) => (
                                            <option value={country.countryName} />
                                        ))}
                                    </datalist>
                                </div>
                            </div>
                            <div className="register-partner-row-input">
                                <label for="username" className="label" >User Name</label>
                                <div>
                                    <input id="username" type="text" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={userNameRef} required />
                                </div>
                            </div>
                        </div>
                        <br />
                        <div className="register-partner-row-inputs">
                            <div className="register-partner-row-input" style={{ marginRight: "2%" }}>
                                <label for="partnername" className="label" >Partner Name</label>
                                <div>
                                    <input id="partnername" type="text" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={partnerNameRef} required />
                                </div>
                            </div>
                            <div className="register-partner-row-input">
                                <label for="address" className="label" >Address</label>
                                <div>
                                    <input id="address" type="text" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={addressRef} required />
                                </div>
                            </div>
                        </div>
                        <br />
                        <div className="register-partner-row-inputs">
                            <div className="register-partner-row-input" style={{ marginRight: "2%" }}>
                                <label for="contactnumber" className="label" >Contact Number</label>
                                <div>
                                    <input id="contactnumber" type="tel" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={contactNumberRef} required />
                                </div>
                            </div>
                            <div className="register-partner-row-input">
                                <label for="emailid" className="label" >Email Id</label>
                                <div>
                                    <input id="emailid" type="text" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={emailRef} required />
                                </div>
                            </div>
                        </div>
                        <br />
                        <div className="register-partner-row-inputs">
                            <div className="register-partner-row-input" style={{ marginRight: "2%" }}>
                                <label for="state" className="label" >State</label>
                                <div>
                                    <input id="state" type="text" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={stateRef} />
                                </div>
                            </div>
                            <div className="register-partner-row-input">
                                <label for="locality" className="label" >Locality</label>
                                <div>
                                    <input id="locality" type="text" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={localityRef} />
                                </div>
                            </div>
                        </div>
                        <br />
                        <div className="register-partner-row-inputs">
                            <div className="register-partner-row-input" style={{ marginRight: "2%" }}>
                                <label for="organizationname" className="label" >Organization Name</label>
                                <div>
                                    <input id="organizationname" type="text" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={organizationNameRef} required />
                                </div>
                            </div>
                            <div className="register-partner-row-input">
                                <label for="commonname" className="label" >Common Name</label>
                                <div>
                                    <input id="commonname" type="text" className="InputField" placeholder="-Text-" min="0" maxlength="30" ref={commonNameRef} required />
                                </div>
                            </div>
                        </div>
                    </div>
                    <ModalFooter className="AddDeviceFooter" style={{ border: "none" }}>
                        <div>
                            <button type="reset" className="secondary-button" onClick={() => { setError(""); setMessagePopup(false); }}>
                                <span className="secondary-button-icon"><ResetIcon /></span>
                                <span className="secondary-button-text">Reset</span>
                            </button>
                            &nbsp;
                            <button type="submit" className="primary-button">
                                <span className="primary-button-icon"><AddIcon /></span>
                                <span className="primary-button-text">{isLoading ? <LoadingIndicator size="small" message="Fetching..." /> : 'Register Partner'}</span>
                            </button>
                        </div>
                    </ModalFooter>
                </form>
            </Modal.Body>
        </Modal >
    );
}

export default AddPartner;